import {
  addBasket,
  addItemToBasket,
  deleteBasket,
  deleteBasketItem,
  getBasket,
  getBasketItems,
  getNewShopOrderNo,
  getSettings,
  updateBasket,
  updateBasketItem,
  updateCustomer,
  writeOrder
} from "@/modules/firestore_repo";
import {
  getServerDate,
  sendOrder,
  simulateOrder
} from "@/modules/functions_repo";
import router from "@/router";

function buildOrderData(state, isIndustryRequired, isWithEndCustomer) {
  return {
    applicationField: state.applicationField ? state.applicationField : null,
    completeShipment: state.deliveryOption
      ? state.deliveryOption.value == "2"
      : null,
    customerNo: state.customerNo,
    customersOrderNo: state.customersOrderNo,
    deliveryAddress: state.shipTo,
    endCustomerAddress: isWithEndCustomer ? state.endCustomer : null,
    industry: isIndustryRequired && state.industry ? state.industry : null,
    lines: state.items.map((item, index) => {
      return {
        materialDescription: item.description,
        materialNo: item.materialNo,
        materialCategory: item.category,
        position: index + 1,
        quantity: item.quantity
      };
    }),
    requestedDeliveryDate: state.reqDeliveryDate,
    salesDocumentType: state.orderType ? state.orderType.value : null,
    vmApplication:
      isIndustryRequired && state.vmApplication ? state.vmApplication : null
  };
}

const emptyAddress = {
  city: "",
  country: "",
  name1: "",
  name2: "",
  name3: "",
  postalCode: "",
  street: "",
  houseNumber: ""
};

const state = {
  alert: null,
  applicationField: null,
  basketId: null,
  customerId: null,
  customerNo: null,
  customersOrderNo: "",
  deliveryOption: null,
  documentCurrency: "",
  endCustomer: emptyAddress,
  industry: null,
  items: [],
  displayMaterialAvailability: false,
  loading: false,
  mostRecentAddedItem: null,
  orderType: null,
  reqDeliveryDate: "",
  sending: false,
  shipTo: emptyAddress,
  snackbar: false,
  userId: null,
  vmApplication: ""
};

const getters = {
  getEndCustomerId: state => {
    return state.endCustomer.id || "";
  },
  getItems: state => {
    if (!state.items || !Array.isArray(state.items)) {
      return [];
    }
    return state.items.map((item, index) => {
      item.position = index + 1;
      return item;
    });
  },
  getShipToId: state => {
    return state.shipTo.id || "";
  },
  itemCount: state => {
    if (!state.items || !Array.isArray(state.items)) {
      return 0;
    }
    let count = 0;
    state.items.forEach(item => {
      count += item.quantity || 0;
    });
    return count;
  },
  mostRecentAddedMaterialNo: state => {
    if (!state.mostRecentAddedItem) {
      return "";
    }
    return state.mostRecentAddedItem.materialNo;
  }
};

const actions = {
  addToBasket: async ({ commit, dispatch, state }, item) => {
    if (!state.basketId) {
      await dispatch("newBasket");
    }
    console.assert(state.basketId, "state.basketId should be truthy");
    const newItem = {
      category: item.category || "",
      description: item.description || "",
      materialNo: item.materialNo || "",
      quantity: Number.parseInt(item.quantity, 10) || 0
    };
    newItem.id = await addItemToBasket(state.basketId, newItem);
    console.assert(newItem.id, "newItem.id should be truthy");
    commit("addItem", newItem);
    commit("setMostRecentAddedItem", newItem);
    commit("setSnackbar", true);
  },
  changeItemQuantity: async ({ commit, state }, { id, quantity }) => {
    await updateBasketItem(state.basketId, id, { quantity });
    commit("changeItemQuantity", { id, quantity });
  },
  deleteBasket: async ({ commit, state }) => {
    const basketId = state.basketId;
    const customerId = state.customerId;
    commit("reset");
    const p = [
      deleteBasket(basketId),
      updateCustomer(customerId, { basketId: null })
    ];
    await Promise.all(p);
  },
  dismissSnackbar: ({ commit }) => {
    commit("setSnackbar", false);
  },
  fetchBasket: async ({ commit, dispatch, state }) => {
    commit("setLoading", true);
    try {
      const data = await getBasket(state.basketId);
      commit("setUserId", data.userId);
      commit("setCustomerId", data.customerId);
      commit("setCustomerNo", data.customerNo);
      commit("setOrderType", data.orderType);
      commit("setDeliveryOption", data.deliveryOption);
      commit("setApplicationField", data.applicationField);
      commit("setIndustry", data.industry);
      commit("setVmApplication", data.vmApplication);
      commit("setCustomersOrderNo", data.customersOrderNo);
      commit("setReqDeliveryDate", data.reqDeliveryDate);
      commit("setShipTo", data.shipTo || emptyAddress);
      commit("setEndCustomer", data.endCustomer || emptyAddress);
      await dispatch("fetchBasketItems");
      await dispatch("fetchDisplayMaterialAvailability");
    } catch (error) {
      commit("reset");
      commit("setAlert", { type: "error", message: error.message || error });
    } finally {
      commit("setLoading", false);
    }
  },
  fetchBasketItems: async ({ commit, state }) => {
    const { items } = await getBasketItems(state.basketId);
    console.assert(items instanceof Array, "items must be an Array");
    commit("setItems", items);
  },
  fetchDisplayMaterialAvailability: async ({ commit }) => {
    const data = await getSettings("_default");
    commit("setDisplayMaterialAvailability", data.displayMaterialAvailability);
  },
  newBasket: async ({ commit, dispatch, rootGetters, rootState }) => {
    const customerId = rootGetters["customer/getCustomerId"];
    const customerNo = rootGetters["customer/getNumber"];
    const basketId = await addBasket({
      userId: rootState.user.id,
      customerId,
      customerNo
    });
    commit("setBasketId", basketId);
    commit("setUserId", rootState.user.id);
    commit("setCustomerId", customerId);
    commit("setCustomerNo", customerNo);
    const p = [
      updateCustomer(customerId, { basketId }),
      dispatch("customer/fetchAddresses", null, { root: true })
    ];
    await Promise.all(p);
  },
  placeOrder: async ({ commit, state, rootGetters }) => {
    commit("setSending", true);
    const clientId = rootGetters["customer/getClientId"];
    const orderData = buildOrderData(
      state,
      rootGetters["customer/isIndustryRequired"],
      rootGetters["customer/isWithEndCustomer"]
    );
    const shopOrderNo = await getNewShopOrderNo(clientId);
    const serverDate = await getServerDate();
    orderData.shopOrderNo = `${clientId}-${shopOrderNo}`;
    orderData.basketId = state.basketId;
    orderData.clientId = clientId;
    orderData.customerName = rootGetters["customer/getName"];
    orderData.customerId = state.customerId;
    orderData.userId = state.userId;
    orderData.timestamp = serverDate;
    const orderId = await writeOrder(orderData);
    const p = [
      sendOrder(orderId, false),
      updateCustomer(state.customerId, { basketId: null })
    ];
    commit("reset");
    router.push("/send_order");
    await Promise.all(p);
  },
  removeFromBasket: async ({ commit, dispatch, state }, id) => {
    await deleteBasketItem(state.basketId, id);
    commit("deleteItem", id);
    if (state.items.length == 0) {
      await dispatch("deleteBasket");
    }
  },
  setApplicationField: async ({ commit, state }, value) => {
    commit("setApplicationField", value);
    await updateBasket(state.basketId, { applicationField: value });
  },
  setCustomersOrderNo: async ({ commit, state }, value) => {
    commit("setCustomersOrderNo", value);
    await updateBasket(state.basketId, { customersOrderNo: value });
  },
  setDeliveryOption: async ({ commit, state }, value) => {
    commit("setDeliveryOption", value);
    await updateBasket(state.basketId, { deliveryOption: value });
  },
  setEndCustomer: async ({ commit, state }, value) => {
    commit("setEndCustomer", value);
    await updateBasket(state.basketId, { endCustomer: value });
  },
  setIndustry: async ({ commit, state }, value) => {
    commit("setIndustry", value);
    await updateBasket(state.basketId, { industry: value });
  },
  setOrderType: async ({ commit, state }, value) => {
    commit("setOrderType", value);
    await updateBasket(state.basketId, { orderType: value });
  },
  setReqDeliveryDate: async ({ commit, state }, value) => {
    commit("setReqDeliveryDate", value);
    await updateBasket(state.basketId, { reqDeliveryDate: value });
  },
  setShipTo: async ({ commit, state }, value) => {
    commit("setShipTo", value);
    await updateBasket(state.basketId, { shipTo: value });
  },
  setVmApplication: async ({ commit, state }, value) => {
    commit("setVmApplication", value);
    await updateBasket(state.basketId, { vmApplication: value });
  },
  simulateOrder: async ({ commit, state, rootGetters }) => {
    const orderData = buildOrderData(
      state,
      rootGetters["customer/isIndustryRequired"],
      rootGetters["customer/isWithEndCustomer"]
    );
    const { documentCurrency, lines, successful } = await simulateOrder(
      state.customerId,
      orderData
    );
    if (successful) {
      commit("setDocumentCurrency", documentCurrency);
      commit("setLineAmounts", lines);
    }
  },
  unsetEndCustomer: async ({ commit, state }) => {
    commit("setEndCustomer", emptyAddress);
    await updateBasket(state.basketId, { endCustomer: null });
  },
  unsetShipTo: async ({ commit, state }) => {
    commit("setShipTo", emptyAddress);
    await updateBasket(state.basketId, { shipTo: null });
  }
};

const mutations = {
  setLoading(state, value) {
    state.loading = value;
  },
  setSending(state, value) {
    state.sending = value;
  },
  addItem: (state, item) => {
    state.items.push(item);
  },
  changeItemQuantity: (state, { id, quantity }) => {
    const index = state.items.findIndex(item => item.id == id);
    state.items[index].quantity = quantity;
    state.items[index].confirmedQuantity = undefined;
    state.items[index].lineAmount = undefined;
  },
  deleteItem: (state, id) => {
    const index = state.items.findIndex(item => item.id == id);
    state.items.splice(index, 1);
  },
  reset: state => {
    state.alert = null;
    state.applicationField = null;
    state.basketId = null;
    state.customerId = null;
    state.customerNo = null;
    state.customersOrderNo = "";
    state.deliveryOption = null;
    state.documentCurrency = "";
    state.endCustomer = emptyAddress;
    state.industry = null;
    state.items = [];
    state.loading = false;
    state.mostRecentAddedItem = null;
    state.orderType = null;
    state.reqDeliveryDate = "";
    state.sending = false;
    state.shipTo = emptyAddress;
    state.snackbar = false;
    state.userId = null;
    state.vmApplication = null;
  },
  setAlert: (state, value) => {
    state.alert = value;
  },
  setApplicationField: (state, value) => {
    state.applicationField = value;
  },
  setLineAmounts: (state, lines) => {
    if (lines instanceof Array) {
      for (let i = 0; i < lines.length; i++) {
        state.items[i].lineAmount = Number.parseFloat(
          lines[i].netValue
        ).toFixed(2);
        state.items[i].confirmedQuantity = lines[i].confirmedQuantity;
      }
    }
  },
  setBasketId: (state, value) => {
    state.basketId = value;
  },
  setCustomerId: (state, value) => {
    state.customerId = value;
  },
  setCustomerNo: (state, value) => {
    state.customerNo = value;
  },
  setCustomersOrderNo: (state, value) => {
    state.customersOrderNo = value || "";
  },
  setDeliveryOption: (state, value) => {
    state.deliveryOption = value;
  },
  setDocumentCurrency: (state, value) => {
    state.documentCurrency = value;
  },
  setEndCustomer: (state, value) => {
    state.endCustomer = value;
  },
  setIndustry: (state, value) => {
    state.industry = value;
  },
  setItems: (state, items) => {
    state.items = items;
  },
  setDisplayMaterialAvailability: (state, value) => {
    state.displayMaterialAvailability = value;
  },
  setMostRecentAddedItem: (state, item) => {
    state.mostRecentAddedItem = item;
  },
  setOrderType: (state, value) => {
    state.orderType = value;
    state.items = state.items.map(item => {
      item.confirmedQuantity = undefined;
      return item;
    });
  },
  setReqDeliveryDate: (state, value) => {
    state.reqDeliveryDate = value || "";
    //TODO
    state.items = state.items.map(item => {
      item.confirmedQuantity = undefined;
      return item;
    });
  },
  setShipTo: (state, value) => {
    state.shipTo = value;
  },
  setSnackbar: (state, value) => {
    state.snackbar = value || false;
  },
  setUserId: (state, value) => {
    state.userId = value;
  },
  setVmApplication: (state, value) => {
    state.vmApplication = value;
  }
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state
};
